<script setup lang="ts">
import { computed, onMounted, ref } from "vue";
import { api } from "@/api/server";
import { auth, logout } from "@/stores/auth";
import type { ConnectionState } from "@/stores/dashboard";
import { formatTime } from "@/utils/format";

const props = defineProps<{
  connection: ConnectionState;
  lastUpdate: Date | null;
}>();

const label = computed(() => {
  switch (props.connection) {
    case "open":
      return `verbunden · Stand ${formatTime(props.lastUpdate)}`;
    case "connecting":
      return "verbinde …";
    case "closed":
      return "getrennt – neuer Versuch in 3 s";
  }
  return "";
});

const leaving = ref(false);
const version = ref("");

// Einmal beim Laden - die Version aendert sich nur mit einem Deploy.
onMounted(async () => {
  try {
    version.value = (await api.version()).version;
  } catch {
    version.value = ""; // ohne Version einfach nichts anzeigen
  }
});

async function signOut(): Promise<void> {
  leaving.value = true;
  try {
    await logout();
  } finally {
    leaving.value = false;
  }
}
</script>

<template>
  <header>
    <span class="dot" :class="{ on: connection === 'open' }" />
    <h1>KinderService · Dashboard</h1>
    <span v-if="version" class="version" title="Version des Servers">v{{ version }}</span>
    <span class="status">{{ label }}</span>

    <span class="user">{{ auth.user }}</span>
    <button class="logout" :disabled="leaving" @click="signOut">Abmelden</button>
  </header>
</template>

<style scoped>
header {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 14px 20px;
  border-bottom: 1px solid var(--line);
  background: var(--panel);
  position: sticky;
  top: 0;
  z-index: 5;
}

h1 {
  font-size: 17px;
  margin: 0;
  font-weight: 600;
}

.dot {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background: var(--danger);
  transition: background 0.2s;
}

.dot.on {
  background: var(--ok);
}

.version {
  color: var(--muted);
  font-size: 12px;
  border: 1px solid var(--line);
  border-radius: 999px;
  padding: 1px 8px;
}

.status {
  color: var(--muted);
  font-size: 13px;
}

.user {
  margin-left: auto;
  color: var(--muted);
  font-size: 13px;
}

.logout {
  padding: 5px 10px;
  font-size: 13px;
}
</style>
